// tslint:disable no-any
import * as _ from 'lodash'
import * as React from 'react'

import { ErrorBoundary, Loader } from '../Common'
import * as T from './Types'

//import { JWT_ID, JWT_REFRESH_TOKEN } from './Authenticated'

/**
 * TODO / FIXME : There is a state/timing issue in this file.
 * When you run the retry fetch loop, it decrements the retries counter
 * by two units per loop.  This means you will get ~half as many retries as
 * you would expect to get based on the setting in file.
 *
 * This is also not a simple thing to fix with the strucuture as-is, so for now,
 * lets just bump this number up to fix the user facing side first (not enough retries).
 *
 * See ticket NAH-1018 for this issue on sprint board.
 */
const defaultProps = {
  retries: 6, // 4 total requests - 1 initial, 3 retries
  spinner: <Loader />,
}

type FetchDefaultProps = Readonly<typeof defaultProps>

interface IFetchProps {
  networkStateError?: string
  networkStatePending?: string
  networkStateSuccess?: string
  children: (data: any, total?: number) => React.ReactNode,
  sdk: any
  spinner?: React.ReactNode
  method: Function
  params: any[]
}

export type FetchProps = IFetchProps & Partial<FetchDefaultProps>

interface FetchState {
  retriesLeft: number
  paginationTotal: number
  requestState: string // 'error' | 'pending' | 'success'
  response: T.JsonResponse | T.JsonErrorResponse | undefined
}

/**
 * A network request helper, utilizing the render props pattern
 * to enable loose coupling between this component, and components that need
 * the data.
 */
export class Fetch extends React.Component<FetchProps, FetchState> {
  public static defaultProps = defaultProps

  public networkStateError: string
  public networkStatePending: string
  public networkStateSuccess: string

  constructor (props: FetchProps) {
    super(props)

    this.networkStateError = _.get(this.props, 'networkStateError', 'error') || 'error'
    this.networkStatePending = _.get(this.props, 'networkStatePending', 'pending') || 'pending'
    this.networkStateSuccess = _.get(this.props, 'networkStateSuccess', 'success') || 'success'

    this.state = {
      paginationTotal: 0,
      requestState: 'pending',
      response: undefined,
      retriesLeft: props.retries || defaultProps.retries,
    }

    _.bindAll(this, [
      'fetchData',
      'renderBody',
      'sdkCallback',
    ])
  }

  public async componentDidMount () {
    try {
      await this.fetchData()
    } catch (reason) {
      console.warn(reason)
    }
  }

  public async componentDidUpdate (prevProps: FetchProps, prevState: FetchState) {
    // Retry a failed network request
    if (prevState.retriesLeft > this.state.retriesLeft) {
      try {
        await this.fetchData()
      } catch (reason) {
        console.warn(reason)
      }
    }
  }

  public render (): React.ReactNode {
    // IDEA: Should we find a way to include the actual children during tests?
    // This would allow components based on data to load properly during tests.
    if (process.env.NODE_ENV === 'test' || window._env_?.NODE_ENV === 'test') {
      return this.props.children(undefined, 0)
    }

    return (
      <ErrorBoundary>
        {this.renderBody()}
      </ErrorBoundary>
    )
  }

  /**
   * Retrieve the request parameters, and make the network call
   */
  public async fetchData () {
    // Skip request during tests
    if (process.env.NODE_ENV === 'test' || window._env_?.NODE_ENV === 'test') {
      this.setState({
        response: {
          data: {},
          offset: 0,
          limit: 50,
          total: 0,
        }
      })

      return
    }

    try {
      const params = [...this.props.params, this.sdkCallback]

      const response: T.JsonResponse = await this.props.method.apply(this.props.sdk, params)

      this.setState({ response })
    } catch (reason) {
      console.warn(reason)
    }
  }

  protected renderBody (response = this.state.response): React.ReactNode {
    // const { response } = this.state

    switch (this.state.requestState) {
      case this.networkStateError:
        const message = _.get(response, 'message', 'Network error')
        console.warn('NETWORK ERROR:', JSON.stringify(response))

        // Handle expired user session
        //const error: string = _.get(response, 'error', '')
        const error: string = ''
        if ('Refresh token is expired!' === error) {
          //sessionStorage.removeItem(KC_ID)
          //sessionStorage.removeItem(JWT_ID)
          //sessionStorage.removeItem(JWT_REFRESH_TOKEN)
          window.location.href = '/'
        }

        // If there are retries left, attempt the request again
        if (this.state.retriesLeft > 0) {
          this.setState(prevState => {
            return (prevState.retriesLeft > 0)
              ? { requestState: 'pending', retriesLeft: prevState.retriesLeft - 1 }
              : null
          })

          return (
            <div className='flex justify-center'>
              <div className='w-16 h-16'>
                { this.props.spinner }
              </div>
            </div>
          )
        }

        return <div>{message}, please refresh and try again...</div>

      case this.networkStateSuccess:
        const data = _.get(response, 'data', response)
        const total = _.get(response, 'total', 0)

        // The state will update before the response is defined,
        // so double-check here
        if (data !== undefined) {
          return this.props.children(data, total)
        } else {
          return (
            <div className='flex justify-center'>
              <div className='w-16 h-16'>
                { this.props.spinner }
              </div>
            </div>
          )
        }

      case this.networkStatePending:
      default:
        return (
          <div className='flex justify-center'>
            <div className='w-16 h-16'>
              { this.props.spinner }
            </div>
          </div>
        )
    }
  }

  protected sdkCallback (state: string) {
    this.setState(() => {
      return {
        requestState: state,
      }
    })
  }
}
